/* Core & libraries */
import React, { useEffect, useState, useRef } from "react";
import { useTheme } from "react-jss";
import { Outlet } from 'react-router-dom';
import { useNavigate, useLocation } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";

/* Helpers, constants, custom methods, internal libraries */
import { useUserContext } from "../../contexts/userContext";
import { useCompaniesService } from "../../services/companiesService.js";
import { usePaymentService } from "services/paymentService";
import { useGlobalContext, useGlobalUpdateContext } from "../../contexts/globalContext.js";
import { useHasPermission } from "../../hooks/useHasPermission";
import { isAdmin, getDismissableBannerKey } from "../../helpers/general.js";
import { PAYMENT_ROUTE, HOME_ROUTE, SETTINGS_ROUTE, AVAILABLE_CREDITS_ROUTE } from "../../constants/routes";
import { AIRLINES, REDIRECT_AA_BANNER_LINK } from "../../constants/appConstants";
import { storageFactory } from "factory/storageFactory";
import { useBannerService } from "services/bannerService";

/* Components */
import Navigation from "../navigation";
import GlobalComponents from "../globalComponents";
import ManagedCompanyBar from "../admin/managedCompanyBar";
import ProfileIncompleteBanner from "../profileIncompleteBanner/index.js";
import IntercomComponent from "../../components/intercom/index";
import UCButton from "../../components/buttons";
import { ReactComponent as ArrowRight } from '../../resources/images/arrow-right-carousel.svg';
import { ReactComponent as AlertIcon } from '../../resources/images/alert-triangle.svg';
import AlertMessage from "../alert";
import GeneralBanner from "../generalBanner/index.js";
import { ReactComponent as ChoiceCupIcon } from '../../resources/images/choice-cup-icon.svg';
import { ReactComponent as SettingsIcon } from '../../resources/images/settings-icon-3-bullet.svg';
import { ReactComponent as SettingsActiveIcon } from '../../resources/images/settings-active.svg';

/* Styles */
import styles from "./styles.js"
import generalStyles from "../../resources/styles/general";
import classNames from 'classnames';

const sessionStore = storageFactory(() => sessionStorage);

const Layout = () => {
  const theme = useTheme();
  const classes = styles({ theme });
  const general = generalStyles({ theme });
  const navigate = useNavigate();
  const location = useLocation();

  const userContext = useUserContext();
  const globalContext = useGlobalContext();
  const { getCompanyBanner, getCompanyDetails, dismissTripCreditCompanyBanner } = useCompaniesService();
  const { getCardsExpiringData } = usePaymentService();
  const { checkPermission } = useHasPermission();
  const { dismissTierBanner } = useBannerService();

  const companyEntityId = userContext?.userInfo?.companyInfo?.companyEntityId;
  const providerCode = userContext?.userInfo?.providerInfo?.providerCode;
  const [bannerResponse, setBannerResponse] = useState({})
  const [dismissedBanners, setDismissedBanners] = useState({});
  const bannersContainerRef = useRef(null);
  const [bannersHeight, setBannersHeight] = useState(0);
  const setGlobalProperties  = useGlobalUpdateContext();

  const handleDismiss = async (key) => {
    sessionStore.setItem(key, "dismissed");
    setDismissedBanners(prev => ({ ...prev, [key]: true }));
    //dismiss trip credit banner AA
    if (key === getDismissableBannerKey(`${providerCode}_2`)) {
      await dismissTripCreditCompanyBanner(
        companyEntityId,
        {
          "notificationType": "ASSIGNMENT",
          "category": "TRIPCREDIT"
        },
      )
    }
    //dismiss payment and policy reminder banner AM
    if (key === getDismissableBannerKey(`${providerCode}_paymentAndPolicy`)) {
      await dismissTripCreditCompanyBanner(
        companyEntityId,
        {
          "notificationType": "RE_ENTER",
          "category": "PAYMENT_AND_POLICY"
        },
      )
    }
  };

  const isBannerVisible = (key) => !dismissedBanners[key] && sessionStore.getItem(key) !== "dismissed";
  const [companyDetails, setCompanyDetails] = useState({})
  const [showTierBannerChoice, setShowTierBannerChoice] = useState(true)
  const providerIsChoice = providerCode === AIRLINES.CHOICE;
  const providerIsAA = providerCode === AIRLINES.AA;
  const providerIsAM = providerCode === AIRLINES.AM;
  const companyMigratedChoice = providerIsChoice && userContext?.forceUpdateCompanyInfo;
  const hideNavigation = companyMigratedChoice && location.pathname.includes(SETTINGS_ROUTE);
  const choiceTierNotification = companyDetails?.notifications;
  const currentTier = companyDetails?.discount?.currentTier?.tierId;
  const currentNights = companyDetails?.discount?.currentNights;
  const tierLevels = companyDetails?.discount?.tierLevels;

  const nextTier = tierLevels?.find(tier => Number(tier.minimumNights) > currentNights);
  const nightsNeeded = nextTier ? Number(nextTier.minimumNights) - currentNights : 0;
  const currentDiscount = tierLevels?.find(level => Number(level.tierId) === Number(choiceTierNotification?.[0]?.params?.currentTierId))?.discount;

  const lastTierDetails = companyDetails?.discount?.tierLevels?.length > 0 
    ? companyDetails.discount.tierLevels.reduce((prev, current) => 
        Number(current.minimumNights) > Number(prev.minimumNights) ? current : prev
      )
    : null;

  const fetchCompanyDetails = async () => {
    const response = await getCompanyDetails(companyEntityId);
    setCompanyDetails({
      ...response,
      companyId: companyEntityId,
    });
  };

  useEffect(() => {
    //get company info for Tier banner for Choice for ATM users
    if ((providerIsChoice || providerIsAA || providerIsAM) && userContext?.userInfo?.companyInfo?.RoleId === "8") {
      (async () => {
        await fetchCompanyDetails()
      })();
    }
  }, []);

  const assignmentNotification = companyDetails?.notifications?.find(
    item => item.notificationType === "ASSIGNMENT"
  );

  const getTripCreditsAssignmentType = () => {
    switch(assignmentNotification?.params?.assignmentType) {
      case "TRUE":
        return <FormattedMessage id="tripcredits.banner.automatic" />
      case "FALSE":
        return <FormattedMessage id="tripcredits.banner.manual" />
      case "PERSONAL":
        return <FormattedMessage id="tripcredits.banner.travelerowned" />
      default:
        return <FormattedMessage id="tripcredits.banner.automatic" />
    }
  }

  const getBannerText = () => {
    if (Object.keys(companyDetails).length === 0) {
       return {
          bannerTitle: <Skeleton count={1} width={130}  />,
          bannerDescription: <Skeleton count={1} width={400} />
        }
    } else {
      if (lastTierDetails?.tierId === currentTier) {
        return {
            bannerTitle: <FormattedMessage id="choiceTierBanner.title" />,
            bannerDescription: <FormattedMessage id="choiceTierBanner.description.lastTier" values={{discount: currentDiscount, currentTier: companyDetails?.discount?.currentTier?.tierId}} />
          }
      }
      
      switch (companyDetails?.notifications?.[0]?.notificationType) {
        case "UPGRADE":
        case "OVERRIDE":
          return {
            bannerTitle: <FormattedMessage id="choiceTierBanner.title" />,
            bannerDescription: <FormattedMessage id="choiceTierBanner.description" values={{currentTier: currentTier, roomNightsNeeded: nightsNeeded}} />
          }
        case "DOWNGRADE":
          return {
            bannerTitle: <FormattedMessage id="choiceTierBanner.title.downgrade" values={{currentTier: currentTier}}/>,
            bannerDescription: <FormattedMessage id="choiceTierBanner.description.downgrade" values={{roomNightsNeeded: nightsNeeded}}/>,
          }
        default:
          return {
            bannerTitle: <FormattedMessage id="choiceTierBanner.title" />,
            bannerDescription: <FormattedMessage id="choiceTierBanner.description" values={{currentTier: currentTier, roomNightsNeeded: nightsNeeded}} />
          }
      }
    }
  }

  const handleDismissTierBanner = async () => {
    setShowTierBannerChoice(false)
    const dismissBannerPayload = {
      notificationType: choiceTierNotification?.[0]?.notificationType,
      category: choiceTierNotification?.[0]?.category
    };

    await dismissTierBanner(dismissBannerPayload);
    await fetchCompanyDetails();
  }

  useEffect(() => {
    if (isAdmin(userContext) || userContext?.manageCompany) {
      return
    }
    (async () => {
      const response = await getCompanyBanner(companyEntityId);
      if (response) {
        setBannerResponse(response)
      }
    })();
  }, [])

  const cardsExpiringBannerData = async () => {
    await getCardsExpiringData(companyEntityId);
  };

  useEffect(() => {
    if (localStorage.getItem("maintenanceMode") !== "true" &&
      !isAdmin(userContext) &&
      checkPermission("F_VIEW_EXPIREDCARD_BANNER")) {
      cardsExpiringBannerData();
    }
  }, [localStorage.getItem("maintenanceMode")]);

  const displayPaymentAndPolicyBannerAM = companyDetails?.notifications?.some(
    notification => notification.category === 'PAYMENT_AND_POLICY'
  ) ?? false;

  const bannerConfig = {
    [AIRLINES.AA]: [
      {
        key: getDismissableBannerKey(providerCode),
        // display only if the env variable present and dont display for countries based in Brazil and for travelers
        enabled: process.env.REACT_APP_DISPLAY_AA_ADVANTAGE_ALERT === "true" && globalContext?.programDetails?.country !== "BR" && userContext?.userInfo?.companyInfo?.RoleId !== 7,
        dismissible: true,
        content: (
          <p className={classes.alertMsg}>
            <span className={classes.alertHeadline}><FormattedMessage id="banner.aadvantage.title"/></span>
            <span className={classes.alertBody}>
              <FormattedMessage id="banner.aadvantage.description"/>
               <a
                className={classes.alertLink}
                target="_blank"
                rel="noopener noreferrer"
                href={REDIRECT_AA_BANNER_LINK}
              >
                <FormattedMessage id="banner.aadvantage.link"/>
              </a>
            </span>
          </p>
        )
      },
      {
        key: getDismissableBannerKey(`${providerCode}_2`),
        enabled: assignmentNotification,
        dismissible: true,
        customStyle: classes.tripCreditAlertBanner,
        content: (
          <p className={classes.tripCreditMessageContainer}>
            <SettingsIcon />
            <FormattedMessage 
              id="tripcredits.banner.description" 
              values={{
                tripSetting: getTripCreditsAssignmentType(),
                firstName: assignmentNotification?.params?.firstName,
                lastName: assignmentNotification?.params?.lastName
              }}
            />
            <Link to={AVAILABLE_CREDITS_ROUTE} className={classes.linkLearnMore} >
              <FormattedMessage id="home.learnMoreButton" />
            </Link>
          </p>
        )
      },
    ],
    [AIRLINES.AS]: [
      {
        key: getDismissableBannerKey(providerCode),
        enabled: process.env.REACT_APP_DISPLAY_AS_ALERT === "true",
        dismissible: false,
        customStyle: classes.asAlertStyle,
        content: (
          <p className={classes.alertMsg}>
            <FormattedMessage id="banner.finishingTouches.AS"/>
          </p>
        )
      }
    ],
    [AIRLINES.AM]: [
      {
        key: getDismissableBannerKey(`${providerCode}_paymentAndPolicy`),
        enabled: !isAdmin(userContext) && displayPaymentAndPolicyBannerAM,
        dismissible: true,
        customStyle: classes.amAlertStyle,
        content: (
          <p className={classes.alertMsgAM}>
            <SettingsActiveIcon />
            <span><FormattedMessage id="banner.existingCards.title.AM"/>{" "}</span>
            <FormattedMessage id="banner.existingCards.description.AM"/>
          </p>
        )
      }
    ]
  }

  const currentAlerts = bannerConfig[providerCode] ?? [];
  const visibleAlertsCount = currentAlerts.filter(a => a.enabled && isBannerVisible(a.key)).length;

  useEffect(() => {
    const el = bannersContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setBannersHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setGlobalProperties({ ...globalContext, fixedHeaderOffset: bannersHeight + (userContext?.manageCompany ? 52 : 0) });
  }, [bannersHeight, userContext?.manageCompany]);

  const { bannerTitle, bannerDescription } = getBannerText() || {};

  return (
      <div className={`${general.main} ${visibleAlertsCount > 0 ? classes.bannerPresentMainStyle : ""}`} style={{ marginTop: `${bannersHeight + (userContext?.manageCompany ? 52 : 0)}px` }}>
        <div className={classNames(classes.bannersContainer, userContext?.manageCompany && classes.bannersContainerWithManagedBar)} ref={bannersContainerRef}>
          {currentAlerts.map((alert) => alert.enabled && isBannerVisible(alert.key) && (
          <div key={alert.key} className={`${alert.customStyle || ""} ${userContext?.manageCompany ? classes.manageCompanyBannerPresent : ""}`}>
            <AlertMessage
              bannerKey={alert.key}
              dismissible={alert.dismissible}
              content={alert.content}
              onDismiss={() => handleDismiss(alert.key)}
              alertStyling={classes.insideAlertStyle}
            />
          </div>
          ))}
        </div>
        {checkPermission("F_INTERCOM_CHAT") && (
          <IntercomComponent/>
        )}

        {bannerResponse.showBanner && globalContext.showIncompleteBanner &&
          <ProfileIncompleteBanner bannerResponse={bannerResponse}/>}
        <ManagedCompanyBar/>
        {
        userContext?.loggedIn && !hideNavigation &&
          <Navigation bannerResponse={ bannerResponse } bannerDisplay={ globalContext.showIncompleteBanner } bannersHeight={ bannersHeight + (userContext?.manageCompany ? 52 : 0) }/>
        }
        {globalContext?.showExpiryCreditCards && !isAdmin(userContext) && providerCode && providerCode !== AIRLINES.UC && (
          <div className={`${general.pageWrapper} ${classes.mainBannerWrapper}`}>
            <div className={classes.alertIcon}><AlertIcon /></div>
            <div className={classes.textWrapper}>
              <p className={classes.firstPar}>
                <FormattedMessage id={`cardExpiringBanner.firstPar.${providerCode}`} />
              </p>

              <p className={classes.secondPar}>
                <FormattedMessage id={`cardExpiringBanner.secondPar.${providerCode}`}/>
              </p>
              <UCButton
                onClick={() => navigate('../' + PAYMENT_ROUTE)}
                type="link"
                className={classes.updateBtn}
                text={<FormattedMessage id={`cardExpiringBanner.updateButton.${providerCode}`}/>}
                iconEnd={<ArrowRight/>}
              />
            </div>
          </div>
        )}
        {providerIsChoice && choiceTierNotification && showTierBannerChoice && !isAdmin(userContext) && (
          <div className={ general.pageWrapper }>
            <GeneralBanner
              title={bannerTitle}
              message={bannerDescription}
              dismissBannerAction={() => handleDismissTierBanner()}
              buttonAction={() => navigate('../' + HOME_ROUTE)}
              closable
              actionableButton={location.pathname !== HOME_ROUTE}
              icon={<ChoiceCupIcon />}
              buttonText={<FormattedMessage id="choiceTierBanner.viewBenefits" />}
              classes={{
                banner: classes.choiceBanner,
                closeIcon: classes.choiceCloseIconBanner,
                bannerTitle: classes.choiceBannerTitle,
                bannerDescription: classes.choiceBannerDescription
              }}
            />
          </div>
        )}
        <div className={classNames(general.pageWrapper, hideNavigation && classes.noNavPageWrapper)}>
          <Outlet />
        </div>
        {
          userContext?.loggedIn &&
            <GlobalComponents />
        }
      </div>
  )
}

export default Layout;
